import React from 'react';
import { Image } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

import * as icons from '@gn-audio/icons';
import { JabraIcon } from '@gn-audio/jss-components';

import {
  IconTabNavigationItem,
  ImageTabNavigationItem,
  TabNavigationItemFields,
  TabNavigationItemType,
} from './tabNavigationSpotTypes';

type TabNavigationItemProps = {
  tab: TabNavigationItemFields;
  index: number;
  navigationType: TabNavigationItemType;
  activeIndex: number | null;
  handleTabClick: (index: number) => void;
  instanceId?: number;
};

const TabNavigationItem: React.FC<TabNavigationItemProps> = ({
  tab,
  index,
  navigationType,
  activeIndex,
  handleTabClick,
  instanceId,
}) => {
  const isActiveTab = activeIndex === index;
  const { text } = tab || {};

  const iconTab = navigationType === 'icon' ? (tab as IconTabNavigationItem) : null;
  const imageTab = navigationType === 'image' ? (tab as ImageTabNavigationItem) : null;

  const activeInactiveClasses = isActiveTab
    ? 'bg-white text-black'
    : 'hover:bg-[var(--tab-darker-bg-color)] hover:text-[var(--tab-darker-text-color)]';

  const activeInactiveClassesIcon = isActiveTab
    ? 'bg-white text-black'
    : 'group-hover:bg-[var(--tab-darker-bg-color)] group-hover:text-[var(--tab-darker-text-color)]';

  const textTabClasses = classNames(
    'm-2 rounded-full px-14 py-2 font-bold transition-colors',
    activeInactiveClasses
  );

  const iconTabClasses = classNames(
    'm-2 paragraph-small font-bold group transition-colors flex-1 md:min-w-[160px] basis-0 grow py-4 px-6 flex flex-col items-center justify-start'
  );

  const imageTabClasses = classNames(
    'flex flex-col m-3 rounded-3xl p-2 font-bold transition-colors',
    activeInactiveClasses
  );

  if (!!iconTab && iconTab?.icon?.value) {
    return (
      <button
        type="button"
        className={iconTabClasses}
        onClick={() => handleTabClick(index)}
        aria-label={text?.value || iconTab.icon.value}
        role="tab"
        aria-selected={isActiveTab}
        aria-controls={`tabpanel-${instanceId}-${index}`}
        id={`tab-${instanceId}-${index}`}
      >
        <div
          className={classNames(
            'mb-2 flex size-14 items-center justify-center rounded-full',
            activeInactiveClassesIcon
          )}
        >
          <JabraIcon Icon={icons[iconTab.icon.value]} className="size-8" />
        </div>
        {text?.value ?? ''}
      </button>
    );
  } else if (!!imageTab && !!Object.keys(imageTab?.image?.value || {}).length) {
    return (
      <button
        type="button"
        className={imageTabClasses}
        onClick={() => handleTabClick(index)}
        role="tab"
        aria-selected={isActiveTab}
        aria-controls={`tabpanel-${instanceId}-${index}`}
        id={`tab-${instanceId}-${index}`}
      >
        <Image
          field={imageTab.image}
          className="h-40 w-60 rounded-3xl object-cover"
          height={155}
          width={155}
        />
        <span className="m-2 md:mb-8 md:mt-5">{text?.value ?? ''}</span>
      </button>
    );
  } else {
    if (!text?.value) return null;

    return (
      <button
        type="button"
        className={textTabClasses}
        onClick={() => handleTabClick(index)}
        role="tab"
        aria-selected={isActiveTab}
        aria-controls={`tabpanel-${instanceId}-${index}`}
        id={`tab-${instanceId}-${index}`}
      >
        {text.value}
      </button>
    );
  }
};

export default TabNavigationItem;
